/**
 * Telas baixadas sob demanda (cada uma num pedaço próprio do código) e pré-carga:
 *  - ao passar o mouse num item do menu, a tela dele começa a baixar (prefetchRoute);
 *  - com o navegador ocioso depois da abertura, o globo da Home e as listas (prefetchIdle).
 * Os mesmos carregadores servem às rotas (routes/index.tsx), então nada é baixado duas vezes.
 */
export const pageLoaders = {
  lists: () => import('../pages/ListsPage'),
  leads: () => import('../pages/LeadsPage'),
  places: () => import('../pages/PlacesPage'),
  placesLists: () => import('../pages/PlacesListsPage'),
  help: () => import('../pages/HelpPage'),
  multimedia: () => import('../pages/MultimediaPage'),
  referral: () => import('../pages/ReferralPage'),
  leadexplorer: () => import('../pages/LeadExplorerPage'),
  subscribe: () => import('../pages/SubscribePage'),
  notFound: () => import('../pages/NotFoundPage'),
  onboarding: () => import('../pages/OnboardingPage'),
  activate: () => import('../pages/ActivatePage'),
  uninstall: () => import('../pages/UninstallPage'),
  unsubscribe: () => import('../pages/UnsubscribePage'),
  account: () => import('../pages/AccountPage'),
  authorize: () => import('../pages/AuthorizePage'),
  whatsappGroups: () => import('../pages/WhatsappGroupsPage'),
} as const;

export type PageKey = keyof typeof pageLoaders;

/** Caminho do menu → tela. */
const ROUTE_PAGES: Record<string, PageKey> = {
  '/emails': 'lists',
  '/phones': 'lists',
  '/places': 'places',
  '/help': 'help',
  '/leadtalk': 'multimedia',
  '/leadmusic': 'multimedia',
  '/referral': 'referral',
  '/leadexplorer': 'leadexplorer',
  '/account': 'account',
  '/account/devices': 'account',
  '/account/preferences': 'account',
  '/ferramentas/grupos-whatsapp': 'whatsappGroups',
  '/tools/whatsapp-groups': 'whatsappGroups',
};

const started = new Set<PageKey>();

export function prefetchPage(page: PageKey): void {
  if (started.has(page)) return;
  started.add(page);
  pageLoaders[page]().catch(() => started.delete(page));
}

/** Pré-carga pela rota (itens do menu). */
export function prefetchRoute(path: string): void {
  const page = ROUTE_PAGES[path];
  if (page) prefetchPage(page);
}

/**
 * Depois da abertura, com o navegador ocioso: o globo da Home (o pedaço mais pesado) e a tela
 * de listas. Devolve a limpeza para o useEffect.
 */
export function prefetchIdle(): () => void {
  const run = (): void => {
    void import('../features/home/GlobeMap').catch(() => undefined);
    prefetchPage('lists');
  };
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(run, { timeout: 4000 });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(run, 1500);
  return () => window.clearTimeout(id);
}
